// Ch76 · pure helpers for the P1 beats panel + P2 repetition strip.
//
// The box has published `scenes[]` (PKT-0602 / migration 089) and LN Pro has
// stored, typed and served it since — with ZERO render sites until now. These
// helpers arrange that data for ContentReviewCard's detail panel. No I/O;
// fixture-testable, same contract as clientQueue.ts.

import type { ContentReviewScene } from "../../services/contentReviewService";

export interface BeatLine {
  /** Stable key for React (scene id). */
  id: string;
  /** "hook · Tour this 2BR before it goes" — role and caption, honest about
   * absence: a missing role reads "(no beat role)", a missing caption
   * "(no caption)". */
  text: string;
}

/**
 * P1 · the beats, in reel order. `offset_sec` null means the scene resolved
 * no clip (it occupies none of the reel) — those sort after the placed ones,
 * in publish order, rather than being silently dropped.
 */
export function beatLines(scenes: ContentReviewScene[] | null | undefined): BeatLine[] {
  if (!scenes || scenes.length === 0) return [];
  return [...scenes]
    .sort((a, b) => {
      const ao = a.offset_sec;
      const bo = b.offset_sec;
      if (ao == null && bo == null) return 0;
      if (ao == null) return 1;
      if (bo == null) return -1;
      return ao - bo;
    })
    .map((s) => ({
      id: s.id,
      text: `${s.beat_role || "(no beat role)"} · ${(s.caption || "").trim() || "(no caption)"}`,
    }));
}

export interface BeatRepetition {
  beat_role: string;
  /** Distinct non-empty captions seen for this role. */
  distinct: number;
  /** Total scenes carrying this role (captionless included in total only). */
  total: number;
  /** The caption used most, with how many times — only when repeated. */
  mostRepeated: { caption: string; count: number } | null;
}

/**
 * P2 · per-beat-role repetition. Computed client-side from what one queue
 * page already holds; the point is "the hook line is the same sentence on 9
 * of 12 reels", which no per-card read can show.
 */
export function beatRepetition(
  scenesByReel: (ContentReviewScene[] | null | undefined)[],
): BeatRepetition[] {
  const byRole = new Map<string, Map<string, number>>();
  const totals = new Map<string, number>();
  for (const scenes of scenesByReel) {
    for (const s of scenes ?? []) {
      const role = s.beat_role || "(no beat role)";
      totals.set(role, (totals.get(role) ?? 0) + 1);
      const caption = (s.caption || "").trim();
      if (!caption) continue;
      const captions = byRole.get(role) ?? new Map<string, number>();
      captions.set(caption, (captions.get(caption) ?? 0) + 1);
      byRole.set(role, captions);
    }
  }
  return [...totals.keys()].sort().map((role) => {
    const captions = byRole.get(role) ?? new Map<string, number>();
    let mostRepeated: { caption: string; count: number } | null = null;
    for (const [caption, count] of captions) {
      if (count > 1 && (!mostRepeated || count > mostRepeated.count)) {
        mostRepeated = { caption, count };
      }
    }
    return {
      beat_role: role,
      distinct: captions.size,
      total: totals.get(role) as number,
      mostRepeated,
    };
  });
}
